import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchProjects } from '../store/slices/projectsSlice';
import ProjectFilters from '../components/Projects/ProjectFilters';
import ProjectGrid from '../components/Projects/ProjectGrid';
import Pagination from '../components/Projects/Pagination';
import { Link } from 'react-router-dom';

const ProjectsPage = () => {
  const dispatch = useAppDispatch();
  const { list } = useAppSelector((state) => state.projects);

  useEffect(() => {
    dispatch(fetchProjects({}));
  }, [dispatch]);

  const handlePageChange = (page: number) => {
    dispatch(fetchProjects({ page }));
  };

  // Calculate priority support percentage (mock for now, should come from voting data)
  const getPrioritySupport = (project: any) => {
    // This would come from actual voting results
    const mockPercentages: Record<string, number> = {
      'agriculture': 57,
      'solar': 43,
      'coastal': 40,
    };
    const category = project.category?.toLowerCase() || '';
    return mockPercentages[category] || Math.floor(Math.random() * 30) + 30;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-[var(--color-default-font)]">
          Learning About the Top Community Projects
        </h1>
        <p className="text-[var(--color-subtext-color)]">
          Citizens recently voted on these top community projects. Learn about each initiative and how they could benefit Antigua & Barbuda.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <ProjectFilters />
          <ProjectGrid projects={list.items} isLoading={list.isLoading} />
          {list.pagination && (
            <Pagination
              currentPage={list.pagination.page || 1}
              totalPages={list.pagination.pages || 1}
              onPageChange={handlePageChange}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* How Participation Works */}
          <div className="bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg p-6">
            <h3 className="font-semibold mb-4 text-[var(--color-default-font)]">How Participation Works</h3>
            <ol className="space-y-3 text-sm">
              <li>
                <span className="font-medium text-[var(--color-default-font)]">1. Learn:</span>
                <span className="text-[var(--color-subtext-color)] ml-2">Review project information.</span>
              </li>
              <li>
                <span className="font-medium text-[var(--color-default-font)]">2. Submit Priority:</span>
                <span className="text-[var(--color-subtext-color)] ml-2">Indicate your preference during a Citizen Priority Session.</span>
              </li>
              <li>
                <span className="font-medium text-[var(--color-default-font)]">3. Transparent Results:</span>
                <span className="text-[var(--color-subtext-color)] ml-2">Allocation results are published after the session closes.</span>
              </li>
              <li>
                <span className="font-medium text-[var(--color-default-font)]">4. Have an Impact:</span>
                <span className="text-[var(--color-subtext-color)] ml-2">Results inform funding and implementation priorities, but do not determine laws or authority.</span>
              </li>
            </ol>
            <Link
              to="/"
              className="mt-4 inline-block w-full text-center px-4 py-2 bg-[var(--color-neutral-100)] text-[var(--color-default-font)] rounded-lg hover:bg-[var(--color-neutral-200)] transition-colors text-sm"
            >
              RETURN TO CITIZENS PORTAL
            </Link>
          </div>

          {/* Participation Guarantees */}
          <div className="bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg p-6">
            <h3 className="font-semibold mb-4 text-[var(--color-default-font)]">Participation Guarantees</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-[var(--color-success-600)]">
                <span>✓</span>
                <span className="text-[var(--color-default-font)]">No political affiliation</span>
              </li>
              <li className="flex items-center gap-2 text-[var(--color-success-600)]">
                <span>✓</span>
                <span className="text-[var(--color-default-font)]">Observed by independent monitors</span>
              </li>
              <li className="flex items-center gap-2 text-[var(--color-success-600)]">
                <span>✓</span>
                <span className="text-[var(--color-default-font)]">No payments required</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;

